import {
    Box,
    Button,
    Grid,
    List,
    ListItem,
    ListItemButton,
    Typography,
    Divider,
    CardContent,
    TablePagination,
} from "@mui/material";
import { AppCard } from '../../components/common/AppCard';
import { RecordContextProvider } from "react-admin";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";

// Material UI Icons
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PriceChangeIcon from "@mui/icons-material/PriceChangeOutlined";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import CalendarTodayIcon from "@mui/icons-material/Today";
import ClassIcon from "@mui/icons-material/Class";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import GetAppIcon from '@mui/icons-material/GetApp';

import { AccountTrade, capitalizeFirst } from "../../types";
import { getFormattedDate, getFormattedTime, universalAddressToSymbol } from "../../types/util";
import { ROW_FLASH_SX } from "../../styles/realtimeAnimations";
import { resolveValuation } from '../../utils/resolveValuation';

interface Props {
    trades: AccountTrade[];
    account_id: String;
    from: Date;
    to: Date;
    currency: "eur" | "usd" | "chf";
    highlightedIds?: Set<string>;
    isCombined?: boolean;
}

export const PortfolioTrades = ({ trades, account_id, from, to, currency, highlightedIds, isCombined }: Props) => {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [csvLoading, setCsvLoading] = useState(false);

    // Reset page when account/data source changes
    useEffect(() => { setPage(0); }, [account_id]);

    // Sort trades by timestamp descending
    const sortedTrades = [...trades].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    // Paginate the sorted trades
    const paginatedTrades = sortedTrades.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getSideIcon = (side: string) => {
        switch (side) {
            case "buy":
                return <KeyboardArrowUpIcon color="success" />;
            case "sell":
                return <KeyboardArrowDownIcon color="error" />;
            default:
                return <SwapHorizIcon />;
        }
    };

    const openTradeLink = (trade: AccountTrade, accountId: String) => {
        const [location] = accountId.split(":");
        const symbol = universalAddressToSymbol(trade.trade.base_asset)

        if (location === "ethereum") {
            window.open(`https://etherscan.io/tx/${trade.transaction_id}`, "_blank");
        } else if (location === "binance") {
            if (symbol) {
                window.open(`https://www.binance.com/en/trade/${symbol.toUpperCase()}_USDC`, "_blank");
            } else {
                console.warn("Missing symbol for Binance link");
            }
        } else if (location === "kraken") {
            if (symbol) {
                window.open(`https://pro.kraken.com/app/trade/${symbol.toLowerCase()}-eur`, "_blank");
            } else {
                console.warn("Missing symbol for Kraken link");
            }
        } else {
            console.warn(`No handler for transfer location: '${location}'`);
        }
    };

    const generateCsv = () => {
        if (!trades || trades.length === 0) return;

        setCsvLoading(true);

        const sortedTrades = [...trades].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

        const headers = ['timestamp', 'transaction_id', 'side', 'base_asset', 'base_amount', 'quote_asset', 'quote_amount', 'price', `base_value-${currency.toLowerCase()}`, `quote_value-${currency.toLowerCase()}`, `cost-${currency.toLowerCase()}`];

        const rows = sortedTrades.map((trade) => {
            const baseValue = trade.trade.base_valuation?.global?.[currency.toLowerCase()] || 0;
            const quoteValue = trade.trade.quote_valuation?.global?.[currency.toLowerCase()] || 0;
            const costValue = trade.cost_valuation?.global?.[currency.toLowerCase()] || 0;

            return [
                trade.timestamp.toISOString(),
                trade.transaction_id,
                trade.trade.side,
                universalAddressToSymbol(trade.trade.base_asset),
                trade.trade.base_amount.toFixed(8),
                universalAddressToSymbol(trade.trade.quote_asset),
                trade.trade.quote_amount.toFixed(8),
                trade.trade.price.toFixed(6),
                baseValue.toFixed(2),
                quoteValue.toFixed(2),
                costValue.toFixed(6),
            ];
        });

        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${cell}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `portfolio-${account_id}-trades-${from.toISOString().split('T')[0]}--${to.toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setCsvLoading(false);
    };

    return (
        <>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SwapHorizIcon fontSize="small" />
                    Trades
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<GetAppIcon />}
                    disabled={csvLoading || trades.length === 0}
                    size="small"
                    onClick={generateCsv}
                    sx={{ width: 120 }}
                >
                    CSV
                </Button>
            </Box>
            <AppCard>
                <CardContent sx={{ paddingX: 0, paddingTop: 0, "&:last-child": { paddingBottom: 0 } }}>

                    {/* Column Labels */}
                    <Grid container alignItems="center" spacing={0} sx={{ paddingX: 2, paddingY: 1, backgroundColor: "action.hover" }}>

                        <Grid item xs={isCombined ? 1 : 1.5}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <SwapHorizIcon fontSize="small" />
                            </Typography>
                        </Grid>
                        {isCombined && (
                            <Grid item xs={1}>
                                <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                    Acct
                                </Typography>
                            </Grid>
                        )}
                        <Grid item xs={2.5}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <ClassIcon fontSize="small" /> Base
                            </Typography>
                        </Grid>
                        <Grid item xs={2.5}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <ClassIcon fontSize="small" /> Quote
                            </Typography>
                        </Grid>
                        <Grid item xs={isCombined ? 2 : 2.5}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <LocalOfferIcon fontSize="small" /> Price
                            </Typography>
                        </Grid>
                        <Grid item xs={1.5}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <WhatshotIcon fontSize="small" /> Cost
                            </Typography>
                        </Grid>
                        <Grid item xs={1.5} sx={{ textAlign: "right" }}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5 }}>
                                <CalendarTodayIcon fontSize="small" />
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Trade Data */}
                    {paginatedTrades.length === 0 ? (
                        <Box sx={{ p: 2 }}>
                            <Typography variant="body2" color="textSecondary">
                                No trades available.
                            </Typography>
                        </Box>
                    ) : (
                        <List dense sx={{ padding: 0, margin: 0 }}>
                            {paginatedTrades.map((trade, index) => (
                                <RecordContextProvider key={trade.transaction_id} value={trade}>
                                    <ListItem disableGutters sx={{ padding: 0, margin: 0 }}>
                                        <ListItemButton
                                            sx={{
                                                flexDirection: "column",
                                                alignItems: "stretch",
                                                paddingY: 0.75,
                                                "&:hover": { backgroundColor: "action.hover" },
                                                ...(highlightedIds?.has(trade.transaction_id) ? ROW_FLASH_SX : {}),
                                            }}
                                            onClick={() => openTradeLink(trade, isCombined ? trade.account_id : account_id)}
                                        >
                                            <Grid container alignItems="center" spacing={0} sx={{ width: "100%", padding: 0, margin: 0 }}>


                                                {/* Side */}
                                                <Grid item xs={isCombined ? 1 : 1.5}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                        {getSideIcon(trade.trade.side)}
                                                        <Typography variant="body2" color="textPrimary" sx={{ textTransform: "capitalize" }}>
                                                            {trade.trade.side}
                                                        </Typography>
                                                    </Box>
                                                </Grid>

                                                {/* Account */}
                                                {isCombined && (
                                                    <Grid item xs={1}>
                                                        <Typography variant="caption" color="textSecondary" noWrap title={trade.account_id}>
                                                            {trade.account_id.split(':')[0]}
                                                        </Typography>
                                                    </Grid>
                                                )}

                                                {/* Base Amount */}
                                                <Grid item xs={2.5}>
                                                    <Typography variant="body2" color="textPrimary">
                                                        {trade.trade.base_amount.toFixed(8)} {universalAddressToSymbol(trade.trade.base_asset)}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {(() => {
                                                            const v = resolveValuation(trade.trade.base_valuation, currency);
                                                            return v != null
                                                                ? `${v.toLocaleString('de-DE', { maximumFractionDigits: 2 })} ${currency.toUpperCase()}`
                                                                : "N/A";
                                                        })()}
                                                    </Typography>
                                                </Grid>

                                                {/* Quote Amount */}
                                                <Grid item xs={2.5}>
                                                    <Typography variant="body2" color="textPrimary">
                                                        {trade.trade.quote_amount.toLocaleString('de-DE', { maximumFractionDigits: 2 })} {universalAddressToSymbol(trade.trade.quote_asset)}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {(() => {
                                                            const v = resolveValuation(trade.trade.quote_valuation, currency);
                                                            return v != null
                                                                ? `${v.toLocaleString('de-DE', { maximumFractionDigits: 2 })} ${currency.toUpperCase()}`
                                                                : "N/A";
                                                        })()}
                                                    </Typography>
                                                </Grid>

                                                {/* Price */}
                                                <Grid item xs={isCombined ? 2 : 2.5}>
                                                    <Typography variant="body2" color="textPrimary">
                                                        {trade.trade.price.toFixed(6)} {universalAddressToSymbol(trade.trade.quote_asset)}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {(() => {
                                                            const v = resolveValuation(trade.trade.quote_valuation, currency);
                                                            return v != null
                                                                ? `${(v / trade.trade.quote_amount).toLocaleString('de-DE', { maximumFractionDigits: 2 })} ${currency.toUpperCase()}`
                                                                : "N/A";
                                                        })()}
                                                    </Typography>
                                                </Grid>

                                                {/* Cost */}
                                                <Grid item xs={1.5}>
                                                    <Typography variant="body2" color="textPrimary">
                                                        {(() => {
                                                            const [asset, amount] = Object.entries(trade.cost)[0] || [];
                                                            return amount !== undefined
                                                                ? `${amount.toFixed(6)} ${universalAddressToSymbol(asset)}`
                                                                : "N/A";
                                                        })()}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {(() => {
                                                            const v = resolveValuation(trade.cost_valuation, currency);
                                                            return v != null
                                                                ? `${v.toLocaleString('de-DE', { maximumFractionDigits: 2 })} ${currency.toUpperCase()}`
                                                                : "N/A";
                                                        })()}
                                                    </Typography>
                                                </Grid>

                                                {/* Timestamp */}
                                                <Grid item xs={1.5} sx={{ textAlign: "right" }}>
                                                    <Typography variant="body2" color="textPrimary">
                                                        {getFormattedDate(trade.timestamp)}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {getFormattedTime(trade.timestamp)}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </ListItemButton>
                                    </ListItem>
                                    {index < paginatedTrades.length - 1 && <Divider />}
                                </RecordContextProvider>
                            ))}
                        </List>
                    )}

                    {/* Pagination */}
                    {trades.length > rowsPerPage && (
                        <TablePagination
                            component="div"
                            count={trades.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            sx={{ borderTop: 1, borderColor: "divider" }}
                        />
                    )}
                </CardContent>
            </AppCard >
        </>
    );
};